import {
  getSignedAttachmentUrl,
  insertAttachmentRow,
  listAttachmentsByMessages,
  uploadAttachment,
} from "@/features/attachments/api/attachments-api";
import { attachmentsQueries } from "@/features/attachments/api/attachments-keys";
import type {
  AttachmentsByMessage,
  LlmAttachmentInput,
  OptimisticAttachment,
  ParsedAttachmentContent,
  ResolvedAttachment,
} from "@/features/attachments/attachments.types";
import { buildLangchainMessageContent } from "@/features/attachments/attachments.utils";
import { docsQueries } from "@/features/docs/api/docs-keys";
import { workspaceBillingQueries } from "@/features/workspaces/api/workspace-billing-keys";
import { supabase } from "@/lib/supabase";
import type { MessagePayload } from "@shared/types/messages";
import type {
  AiChatTurnStatusView,
  AiMember,
  ChatMessageWithAuthorView,
  ContainerMember,
  Profile,
} from "@shared/types/supabase.types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import {
  AiCreditsExhaustedError,
  AiTurnRequestError,
  cancelAiTurns,
  classifyTurnFailure,
  requestAiTurns,
  triggerSummarization,
} from "../api/chat-ai";
import type {
  AcceptedTurn,
  AiStreamMetaEvent,
  CreditWarningMetaEvent,
  StreamingStep,
} from "../api/chat-ai";
import { getChatMemory, getMessage } from "../api/chat-api";
import {
  upsertMessageInRoomCache,
  useAiChatTurns,
  useStoreMessageMutation,
} from "../api/chat-hooks";
import { chatQueries } from "../api/chat-keys";
import {
  getActivePersona,
  getMentionCandidates,
  isHandleMentioned,
  selectRespondingPersonas,
} from "../chat-ai-routing";
import type { ChatMessageWithAuthorInsert } from "../chat.types";

type Props = {
  containerMember: ContainerMember | undefined;
  aiPersonas: AiMember[] | undefined;
  allMessages: ChatMessageWithAuthorView[];
  roomId: string;
  profile: Profile | undefined;
  projectId: string;
  docsBaseUrl?: string;
  workspaceId?: string;
  /**
   * The room's container type (CR-25). "room" = AI Chat (AI-first: the active
   * persona replies even without an @mention); "message_room" = Message room
   * (human-first: AI replies only when @mentioned).
   */
  roomContainerType?: "room" | "message_room";
  /**
   * Project-level AI personas (CR-25). When a room shares all project personas
   * (isAiShared), @mentions resolve against these rather than the explicit room
   * members.
   */
  projectAiPersonas?: AiMember[];
  /**
   * Whether the room participates all project AI personas (CR-25 follow-up).
   * When true, any project persona is @mentionable; when false, only the room's
   * explicit AI members are. Applies to both AI Chats and Message rooms.
   */
  isAiShared?: boolean;
  /**
   * The room's selected default AI persona (`containers.default_ai_persona_id`,
   * CR-46) — who answers an un-mentioned message in an AI Chat. Re-resolved
   * against the room's participating personas on every send, so a stale or
   * foreign id simply falls back to the kairo/first rule. Ignored in message
   * rooms, which stay human-first.
   */
  defaultAiPersonaId?: string | null;
};

/**
 * An AI reply that failed to generate. Surfaced inline where the reply would
 * have appeared (CR-25) — primarily for Message rooms, where a failed
 * @mention would otherwise silently produce nothing.
 *
 * CR-141: two sources. A request the server never accepted is recorded
 * locally and keeps what a Retry needs (`messageId`, `userMessage`). A durable
 * turn the server gave up on comes from the room's turn rows; it has no
 * Retry (`retryable: false`) — the server already retried it.
 */
export type FailedAiTurn = {
  id: string;
  aiPersona: AiMember;
  message: string;
  // Retained so a retry can re-send the exact same request (local failures only).
  messageId?: string;
  userMessage?: MessagePayload[];
  webSearchEnabled?: boolean;
  // CR-158: set when the turn was refused because the workspace has no AI
  // credits left. Not retryable — the UI explains it instead.
  // CR-160: `provider_error` — the AI provider failed (empty balance, bad key,
  // outage) and no reply row was saved; `retryable` says whether Retry shows.
  kind?: "credits_exhausted" | "provider_error";
  retryable?: boolean;
};

type SendVars = {
  content: string;
  messageId: string;
  attachments?: File[];
};

type AiSendVars = {
  userMessage: MessagePayload[];
  /** The human message the personas answer (CR-141: the accept key). */
  messageId: string;
  aiPersonas: AiMember[];
  webSearchEnabled: boolean;
};

type SendHumanMessageResult = {
  userMessage: MessagePayload[] | null;
  resolvedAttachments: ResolvedAttachment[];
};

type SendHumanMessageContext = {
  previousAttachments?: AttachmentsByMessage;
  objectUrls: string[];
};

type StreamingMessage = ChatMessageWithAuthorView & {
  steps?: StreamingStep[];
  // Transient reasoning shown while the model is "thinking" (between/within
  // tool-calling turns). Cleared once the final answer starts streaming.
  reasoning?: string;
  thinking?: boolean;
  /** CR-126: set on the local placeholder until the persisted row replaces it. */
  streaming?: boolean;
  /** CR-141: the human message this bubble answers (local bubbles only). */
  humanMessageId?: string;
  /**
   * CR-172: the user stopped this reply — the bubble renders "Response
   * stopped" until the persisted placeholder (or a reply saved before the
   * stop landed) replaces it.
   */
  stopped?: boolean;
};

/** CR-172: one durable turn to stop, whichever source named it. */
type TurnToStop = {
  turnId: string;
  aiMessageId: string;
  aiPersonaId: string | null;
  /** The turn row's created_at, when known — where the placeholder sorts. */
  createdAt?: string | null;
};

/**
 * CR-172: this client's in-flight AI request for one human message. Mutable
 * on purpose: the stream callbacks and the Stop handler share it synchronously.
 */
type LocalAiRequest = {
  messageId: string;
  pendingId: string;
  /** The bubble the stream writes into (pendingId until `accepted`). */
  bubbleId: string;
  personas: AiMember[];
  /** Known from the `accepted` frame or the 202 body. */
  turns: AcceptedTurn[];
  stopRequested: boolean;
  /** The cancel for this request's turns was posted (or nothing was sent). */
  stopSettled: boolean;
  /** Aborts the observer fetch after a Stop; never stops the turn itself. */
  observer: AbortController;
};

/**
 * An empty, streaming AI bubble for `persona` in `roomId` — the local
 * placeholder a reply streams into (or a durable turn's thinking bubble).
 */
function aiBubble(
  id: string,
  persona: AiMember,
  roomId: string,
  overrides: Partial<StreamingMessage> = {},
): StreamingMessage {
  return {
    id,
    content: "",
    room: roomId,
    container_member_id: persona.container_member_id,
    actor_id: persona.actor_id,
    actor_type: "ai",
    ai_persona_id: persona.ai_persona_id,
    created_at: new Date().toISOString(),
    full_name: persona.ai_persona_display_name,
    user_id: null,
    avatar_url: persona.ai_persona_avatar_url,
    email: null,
    first_name: null,
    last_name: null,
    // CR-69: an AI persona replying in chat is not an agent acting for a
    // human — that marker is only for MCP-posted document comments.
    metadata: null,
    streaming: true,
    ...overrides,
  };
}

/** CR-172: the "Response stopped" placeholder for a stopped reply. */
function stoppedBubble(
  id: string,
  persona: AiMember,
  roomId: string,
  createdAt?: string | null,
): StreamingMessage {
  return aiBubble(id, persona, roomId, {
    stopped: true,
    streaming: false,
    ...(createdAt ? { created_at: createdAt } : {}),
  });
}

const toTurnToStop = (turn: AcceptedTurn): TurnToStop => ({
  turnId: turn.turnId,
  aiMessageId: turn.aiMessageId,
  aiPersonaId: turn.aiPersonaId,
});

const rowToTurnToStop = (turn: AiChatTurnStatusView): TurnToStop[] =>
  turn.id && turn.ai_message_id
    ? [
        {
          turnId: turn.id,
          aiMessageId: turn.ai_message_id,
          aiPersonaId: turn.ai_persona_id,
          createdAt: turn.created_at,
        },
      ]
    : [];

/** CR-141: a turn that has not ended yet (the only kind a Stop applies to). */
const isOpenTurn = (turn: AiChatTurnStatusView) =>
  turn.status === "queued" || turn.status === "running";

/** CR-141: a local bubble shown until the server says which turn it is. */
const pendingBubbleId = (messageId: string) => `pending-${messageId}`;

type WorkspaceCreditState = {
  workspace_id: string;
  balance_remaining: number;
  balance_unit: string;
  threshold_warning: number;
  threshold_sync: number;
  threshold_hard_stop: number;
  threshold_unit: string;
  conversion_per_unit: number | null;
  is_ai_blocked: boolean;
  last_reconciled_at: string | null;
  last_alert_sent_at: string | null;
  updated_at: string;
};

export function useRealtimeChat({
  containerMember,
  aiPersonas,
  allMessages,
  roomId,
  profile,
  projectId,
  docsBaseUrl,
  workspaceId,
  roomContainerType,
  projectAiPersonas,
  isAiShared,
  defaultAiPersonaId,
}: Props) {
  const queryClient = useQueryClient();
  const storeMessageMutation = useStoreMessageMutation();
  const [streamingMessages, setStreamingMessages] = useState<StreamingMessage[]>([]);
  const [creditWarning, setCreditWarning] = useState<CreditWarningMetaEvent | null>(null);
  // CR-172: Stop state. The ref is read synchronously by stream callbacks
  // (a chunk after a Stop is dropped); the state drives rendering.
  const localRequestsRef = useRef(new Map<string, LocalAiRequest>());
  const stoppedIdsRef = useRef(new Set<string>());
  const [stoppedIds, setStoppedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [cancelsInFlight, setCancelsInFlight] = useState(0);

  const sendHumanMessageMutation = useMutation<
    SendHumanMessageResult | null,
    Error,
    SendVars,
    SendHumanMessageContext | undefined
  >({
    mutationFn: async ({ content, attachments, messageId }) => {
      if (!roomId || !profile || !containerMember) return null;

      const {
        data: { session },
      } = await supabase.auth.getSession();
      // @wc-ignore
      const authToken = session?.access_token ? `Bearer ${session.access_token}` : undefined;

      let input;

      const fullName =
        profile.first_name?.trim() && profile.last_name?.trim()
          ? `${profile.first_name.trim()} ${profile.last_name.trim()}`
          : null;

      const message: ChatMessageWithAuthorInsert = {
        id: messageId,
        content,
        room: roomId,
        container_member_id: containerMember.id,
        actor_id: containerMember.actor_id,
        actor_type: "user",
        user_id: profile.user_id,
        email: profile.email,
        first_name: profile.first_name,
        last_name: profile.last_name,
        avatar_url: profile.avatar_url,
        ai_persona_id: null,
        full_name: fullName,
        // CR-69: the person is typing this themselves — no agent provenance.
        metadata: null,
      };
      await storeMessageMutation.mutateAsync(message);

      const insertedAttachments = [];
      let resolvedAttachments: ResolvedAttachment[] = [];

      // Upload attachments to bucket
      if (attachments?.length) {
        for (const attachment of attachments) {
          const uploaded = await uploadAttachment({
            containerId: roomId,
            messageId,
            file: attachment,
          });

          // Insert attachment rows to attachments table
          const inserted = await insertAttachmentRow({
            messageId,
            containerId: roomId,
            storagePath: uploaded.storagePath,
            fileName: uploaded.fileName,
            mimeType: uploaded.mimeType,
            sizeBytes: uploaded.sizeBytes,
            createdBy: containerMember.id,
          });

          insertedAttachments.push(inserted);
        }
        resolvedAttachments = await Promise.all(
          insertedAttachments.map(async (attachment) => {
            const signedUrl = await getSignedAttachmentUrl({
              storagePath: attachment.storage_path,
            });

            return {
              ...attachment,
              signedUrl,
            };
          }),
        );
        const llmAttachments: LlmAttachmentInput[] = resolvedAttachments.map((attachment) => ({
          id: attachment.id,
          fileName: attachment.file_name,
          mimeType: attachment.mime_type,
          signedUrl: attachment.signedUrl,
        }));

        input = [
          {
            role: "user",
            content: await buildLangchainMessageContent({
              content,
              attachments: llmAttachments,
              authToken,
            }),
          },
        ];

        return {
          userMessage: input,
          resolvedAttachments,
        };
      }

      input = [
        {
          role: "user",
          content,
        },
      ];

      return {
        userMessage: input,
        resolvedAttachments,
      };
    },
    onMutate: ({ attachments, messageId }) => {
      if (!roomId || !attachments?.length) return;

      const key = attachmentsQueries.byRoom(roomId, []).queryKey;
      const previousAttachments = queryClient.getQueryData<AttachmentsByMessage>(key);

      const optimisticAttachments: OptimisticAttachment[] = attachments.map((file, index) => ({
        id: `optimistic-${messageId}-${index}`,
        message_id: messageId,
        file_name: file.name,
        size_bytes: file.size,
        mime_type: file.type || null,
        signedUrl: URL.createObjectURL(file),
        created_at: new Date().toISOString(),
        isUploading: true,
      }));

      queryClient.setQueryData<AttachmentsByMessage>(key, (current = {}) => ({
        ...current,
        [messageId]: optimisticAttachments,
      }));

      return {
        previousAttachments,
        objectUrls: optimisticAttachments.map((attachment) => attachment.signedUrl),
      };
    },
    onSuccess: (data, { messageId }, context) => {
      if (!roomId || !data) return;

      const key = attachmentsQueries.byRoom(roomId, []).queryKey;
      queryClient.setQueryData<AttachmentsByMessage>(key, (current = {}) => ({
        ...current,
        [messageId]: data.resolvedAttachments,
      }));

      for (const objectUrl of context?.objectUrls ?? []) {
        URL.revokeObjectURL(objectUrl);
      }
    },
    onError: (_, __, context) => {
      if (!roomId) return;

      queryClient.setQueryData(
        attachmentsQueries.byRoom(roomId, []).queryKey,
        context?.previousAttachments,
      );

      for (const objectUrl of context?.objectUrls ?? []) {
        URL.revokeObjectURL(objectUrl);
      }
    },
  });

  // CR-199: an AI chat is named by the server before its first turn completes,
  // so a finished turn refreshes the header and the sidebar list. Message
  // rooms are never auto-named.
  const { data: openAiTurns } = useAiChatTurns(roomId, !!roomId, {
    refreshParentOnTurnEnd: roomContainerType === "message_room" ? undefined : projectId,
  });

  // CR-158: mirror a credit_warning (streamed, or replayed from a 402) into the
  // cached workspace credit state and the composer's warning banner.
  const applyCreditMeta = (meta: AiStreamMetaEvent) => {
    let normalizedStatus: CreditWarningMetaEvent["status"] = meta.status;

    if (workspaceId) {
      const cacheKey = workspaceBillingQueries.creditState(workspaceId).queryKey;

      queryClient.setQueryData<WorkspaceCreditState | null>(cacheKey, (previous) => {
        const timestamp = new Date().toISOString();
        // CR-158: a replayed manual-block 402 is blocked whatever the
        // balance, even with no cached row (plain members never have one).
        const manualBlock = (previous?.is_ai_blocked ?? false) || meta.reason === "manual_block";
        const fallbackThreshold = Number.isFinite(meta.thresholdValue)
          ? meta.thresholdValue
          : (previous?.threshold_hard_stop ??
            previous?.threshold_sync ??
            previous?.threshold_warning ??
            0);
        const inferredHardStop = previous?.threshold_hard_stop ?? fallbackThreshold;

        if (meta.status === "blocked" && !manualBlock && meta.balanceRemaining > inferredHardStop) {
          normalizedStatus = "sync";
        }

        const warningThreshold =
          normalizedStatus === "warning"
            ? fallbackThreshold
            : (previous?.threshold_warning ?? fallbackThreshold);

        const syncThreshold =
          normalizedStatus === "sync"
            ? fallbackThreshold
            : (previous?.threshold_sync ?? fallbackThreshold);

        const hardStopThreshold =
          normalizedStatus === "blocked" ? fallbackThreshold : inferredHardStop;

        return {
          workspace_id: workspaceId,
          balance_remaining: meta.balanceRemaining,
          balance_unit: previous?.balance_unit ?? meta.unit,
          threshold_warning: warningThreshold,
          threshold_sync: syncThreshold,
          threshold_hard_stop: hardStopThreshold,
          threshold_unit: previous?.threshold_unit ?? meta.unit,
          conversion_per_unit: previous?.conversion_per_unit ?? null,
          is_ai_blocked: manualBlock,
          last_reconciled_at: meta.lastReconciledAt ?? previous?.last_reconciled_at ?? null,
          last_alert_sent_at: previous?.last_alert_sent_at ?? null,
          updated_at: timestamp,
        };
      });
    }

    setCreditWarning({
      ...meta,
      status: normalizedStatus,
    });
  };

  const sendAiMessageMutation = useMutation<boolean, Error, AiSendVars>({
    mutationFn: async ({ userMessage, messageId, aiPersonas: personas, webSearchEnabled }) => {
      if (!roomId || !containerMember || !personas.length) return false;

      // CR-141: the local bubble starts as a "pending" placeholder for the first
      // persona and becomes the streamed turn's bubble (keyed by its
      // aiMessageId) once the server's `accepted` frame names it. CR-172: it
      // shows from the start of the send, so Stop is offered while the history
      // is still being prepared.
      const pendingId = pendingBubbleId(messageId);
      const request: LocalAiRequest = {
        messageId,
        pendingId,
        bubbleId: pendingId,
        personas,
        turns: [],
        stopRequested: false,
        stopSettled: false,
        observer: new AbortController(),
      };
      localRequestsRef.current.set(messageId, request);
      const bubbleFor = (id: string, persona: AiMember): StreamingMessage =>
        aiBubble(id, persona, roomId, { humanMessageId: messageId });
      const updateBubble = (update: (message: StreamingMessage) => StreamingMessage) => {
        // CR-172: nothing reaches a reply the user stopped.
        if (request.stopRequested) return;
        setStreamingMessages((prev) =>
          prev.map((message) => (message.id === request.bubbleId ? update(message) : message)),
        );
      };
      setStreamingMessages((prev) => [
        ...prev.filter((message) => message.id !== pendingId),
        bubbleFor(pendingId, personas[0]),
      ]);

      const RESUMMARY_INTERVAL = 10;
      const aiTurnsKey = chatQueries.aiTurns(roomId).queryKey;
      let chatMemory: Awaited<ReturnType<typeof getChatMemory>> = null;
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        // @wc-ignore
        const authToken = session?.access_token ? `Bearer ${session.access_token}` : undefined;

        // Fetch stored summary for this room (non-blocking — failure is graceful)
        chatMemory = await getChatMemory(roomId);

        const allHistory = allMessages.slice(0, -1); // exclude the current user message

        // message_count = number of messages covered by the summary.
        // Slice from that index so recentHistory covers exactly what the summary doesn't.
        // When no summary exists (lastSummarizedMessageIndex = 0), all history is included verbatim.
        const lastSummarizedMessageIndex = chatMemory?.message_count ?? 0;
        const FALLBACK_WINDOW = 30;
        const recentHistoryMessages = allHistory.slice(
          lastSummarizedMessageIndex || Math.max(0, allHistory.length - FALLBACK_WINDOW),
        );
        const attachmentsByMessage: Record<string, LlmAttachmentInput[]> = {};
        const messageIds = recentHistoryMessages
          .map((message) => message.id)
          .filter((id): id is string => !!id);

        const messageAttachments = await listAttachmentsByMessages({
          messageIds,
        });

        await Promise.all(
          messageAttachments.map(async (attachment) => {
            const attachmentMessageId = attachment.message_id;

            const resolved: LlmAttachmentInput = {
              id: attachment.id,
              fileName: attachment.file_name,
              mimeType: attachment.mime_type,
              signedUrl: await getSignedAttachmentUrl({
                storagePath: attachment.storage_path,
              }),
              parsedContent: attachment.parsed_content as ParsedAttachmentContent | null,
            };
            attachmentsByMessage[attachmentMessageId] ??= [];
            attachmentsByMessage[attachmentMessageId].push(resolved);
          }),
        );

        const recentHistory: MessagePayload[] = await Promise.all(
          recentHistoryMessages.map(async (message) => ({
            role: message.actor_type === "ai" ? "assistant" : "user",
            content: await buildLangchainMessageContent({
              content: `${message.full_name ?? "Unknown"}: ${message.content}`,
              attachments: message.id ? (attachmentsByMessage[message.id] ?? []) : [],
              authToken,
            }),
          })),
        );

        const history: MessagePayload[] = chatMemory?.summary
          ? [
              {
                role: "user",
                content: `[Conversation memory — summary of earlier messages: ${chatMemory.summary}]`,
              },
              ...recentHistory,
            ]
          : recentHistory;

        // CR-172: stopped before anything was sent — nothing to cancel.
        if (request.stopRequested) return true;

        const result = await requestAiTurns(
          {
            roomId,
            containerId: projectId,
            actorId: containerMember.actor_id,
            messageId,
            aiPersonaIds: personas.map((persona) => persona.ai_persona_id),
            messages: [...history, ...userMessage],
            webSearchEnabled,
            docsBaseUrl,
          },
          {
            onAccepted: ({ turnId, aiMessageId, turns }) => {
              const personaId = turns.find((turn) => turn.turnId === turnId)?.aiPersonaId;
              // CR-172: never empty once accepted — the streamed turn at least.
              request.turns = turns.length
                ? turns
                : [
                    {
                      turnId,
                      aiMessageId,
                      aiPersonaId: personaId ?? personas[0].ai_persona_id,
                      turnIndex: 0,
                      status: "running",
                    },
                  ];
              if (request.stopRequested) {
                // CR-172: the Stop came before the server named the turns.
                stopRequestTurns(request, request.turns.map(toTurnToStop));
                return;
              }
              const persona =
                personas.find((candidate) => candidate.ai_persona_id === personaId) ?? personas[0];
              setStreamingMessages((prev) =>
                prev.map((message) =>
                  message.id === pendingId ? bubbleFor(aiMessageId, persona) : message,
                ),
              );
              request.bubbleId = aiMessageId;
              // The other turns of this message show from the turn rows.
              void queryClient.invalidateQueries({ queryKey: aiTurnsKey });
            },
            onChunk: (partialText) => {
              updateBubble((message) => ({ ...message, content: partialText }));
            },
            onMeta: applyCreditMeta,
            onStep: ({ tool, label, status }) => {
              updateBubble((message) => {
                const steps = message.steps ?? [];
                if (status === "running") {
                  return { ...message, steps: [...steps, { tool, label: label ?? tool, status }] };
                }
                return {
                  ...message,
                  steps: steps.map((s) =>
                    s.tool === tool && s.status === "running" ? { ...s, status: "done" } : s,
                  ),
                };
              });
            },
            onInvalidate: ({ resource, containerId: invalidatedContainerId }) => {
              if (resource === "documents") {
                void queryClient.invalidateQueries({
                  queryKey: docsQueries.byContainer(invalidatedContainerId).queryKey,
                });
              }
              if (resource === "chats") {
                void queryClient.invalidateQueries({
                  queryKey: chatQueries.roomsByParent(invalidatedContainerId).queryKey,
                });
              }
            },
            onThinking: (reasoning, active) => {
              updateBubble((message) => ({ ...message, reasoning, thinking: active }));
            },
          },
          { signal: request.observer.signal },
        );
        // CR-220: a "suppressed" answer has no turns — nothing to show or stop,
        // and the finally below drops the pending bubble without a failure card.
        if (!request.turns.length) request.turns = result.turns;

        // CR-37: the server persisted the streamed reply under aiMessageId —
        // pull the authoritative row into the cache before the bubble goes
        // (CR-172: a stopped turn's "Response stopped" row too). Anything
        // else (a dropped stream, a requeued or failed turn, the other
        // personas) is shown from the turn rows: the stream was only an
        // observer, so none of it is a failure of this send.
        if (
          result.kind === "streamed" &&
          result.aiMessageId &&
          (result.end === "final" || result.end === "provider_error" || result.end === "cancelled")
        ) {
          try {
            upsertMessageInRoomCache(queryClient, await getMessage(result.aiMessageId));
          } catch {
            // Not persisted: the turn row says what happens next.
          }
        }
      } finally {
        // Refetch the turn rows BEFORE dropping the local bubble, so a turn
        // that is still running swaps to its row's bubble without a gap.
        await queryClient.invalidateQueries({ queryKey: aiTurnsKey });
        if (request.stopRequested && !request.stopSettled) {
          // CR-172: stopped before the server named the turns — they are in
          // the answer, or (the answer was lost) in the refreshed rows. None
          // at all means nothing was queued: the stopped bubble just goes.
          const turns: TurnToStop[] = request.turns.length
            ? request.turns.map(toTurnToStop)
            : (queryClient.getQueryData<AiChatTurnStatusView[]>(aiTurnsKey) ?? [])
                .filter((turn) => turn.human_message_id === messageId && isOpenTurn(turn))
                .flatMap(rowToTurnToStop);
          stopRequestTurns(request, turns);
        }
        localRequestsRef.current.delete(messageId);
        setStreamingMessages((prev) =>
          prev.filter(
            (message) =>
              message.stopped || (message.id !== pendingId && message.id !== request.bubbleId),
          ),
        );
      }

      // Background summarization — fire-and-forget, never blocks the chat
      // message_count stores how many messages the current summary covers.
      // We re-summarize once RESUMMARY_INTERVAL new messages have accumulated beyond
      // that covered set, ensuring the gap between summary and recent history stays small.
      // The factor-of-2 threshold is necessary because message_count = totalMessages - RESUMMARY_INTERVAL
      // at the time of last summarization, so a threshold of RESUMMARY_INTERVAL would always be true.
      const totalMessages = allMessages.length;
      const lastSummarizedCount = chatMemory?.message_count ?? 0;
      if (totalMessages - lastSummarizedCount >= RESUMMARY_INTERVAL * 2) {
        const messagesToSummarize = allMessages
          .slice(lastSummarizedCount, totalMessages - RESUMMARY_INTERVAL)
          .map((m) => ({
            role: m.actor_type === "ai" ? "assistant" : "user",
            content: `${m.full_name ?? "Unknown"}: ${m.content}`,
          }));
        if (messagesToSummarize.length > 0) {
          void triggerSummarization(
            roomId,
            messagesToSummarize,
            chatMemory?.summary ?? null,
            lastSummarizedCount,
          );
        }
      }

      return true;
    },
  });

  const sendAskUserAnswer = async (
    answer: string,
    aiPersona: AiMember,
    webSearchEnabled: boolean,
    skipAiTurn?: boolean,
  ): Promise<void> => {
    if (!roomId || !profile || !containerMember) return;

    const fullName =
      profile.first_name?.trim() && profile.last_name?.trim()
        ? `${profile.first_name.trim()} ${profile.last_name.trim()}`
        : null;

    const messageId = crypto.randomUUID();
    const message: ChatMessageWithAuthorInsert = {
      id: messageId,
      content: answer,
      room: roomId,
      container_member_id: containerMember.id,
      actor_id: containerMember.actor_id,
      actor_type: "user",
      user_id: profile.user_id,
      email: profile.email,
      first_name: profile.first_name,
      last_name: profile.last_name,
      avatar_url: profile.avatar_url,
      ai_persona_id: null,
      full_name: fullName,
      // CR-69: the person is typing this themselves — no agent provenance.
      metadata: null,
    };
    await storeMessageMutation.mutateAsync(message);

    if (skipAiTurn) return;

    // CR-197: keep the composer's web search toggle (was hard-coded off).
    await runAiTurns([aiPersona], [{ role: "user", content: answer }], webSearchEnabled, messageId);
  };

  const [failedAiTurns, setFailedAiTurns] = useState<FailedAiTurn[]>([]);

  // Ask the server to answer one human message with the given personas,
  // capturing a request it never accepted as an inline, retryable card
  // instead of letting it bubble to the global send-error banner (CR-25).
  const runAiTurns = async (
    personas: AiMember[],
    userMessage: MessagePayload[],
    webSearchEnabled: boolean,
    messageId: string,
  ) => {
    try {
      await sendAiMessageMutation.mutateAsync({
        userMessage,
        messageId,
        aiPersonas: personas,
        webSearchEnabled,
      });
    } catch (error) {
      if (stoppedIdsRef.current.has(pendingBubbleId(messageId))) {
        // CR-172: the user stopped this request — its failure is moot.
        sendAiMessageMutation.reset();
        return;
      }
      recordFailedAiTurns(error, personas, userMessage, webSearchEnabled, messageId);
    }
  };

  // Record one inline card per persona of a request the server did not
  // accept, tagged with its kind when the failure is a typed refusal (CR-158).
  function recordFailedAiTurns(
    error: unknown,
    personas: AiMember[],
    userMessage: MessagePayload[],
    webSearchEnabled: boolean,
    messageId: string,
  ): void {
    const creditsExhausted = error instanceof AiCreditsExhaustedError;
    // A 400 is the server refusing this payload (e.g. over its size limits):
    // resending the identical request can only fail again, so no Retry.
    const invalidRequest = error instanceof AiTurnRequestError && error.status === 400;
    const message =
      error instanceof AiTurnRequestError && !invalidRequest
        ? "The request could not be sent. Please try again."
        : "The assistant could not respond.";
    setFailedAiTurns((prev) => [
      ...prev,
      ...personas.map(
        (aiPersona): FailedAiTurn => ({
          id: crypto.randomUUID(),
          aiPersona,
          messageId,
          userMessage,
          webSearchEnabled,
          message,
          kind: creditsExhausted ? "credits_exhausted" : undefined,
          ...(invalidRequest ? { retryable: false } : {}),
        }),
      ),
    ]);
    if (creditsExhausted && workspaceId) {
      // Refetch the real credit state: the replayed meta only approximates
      // the row (and a legacy 402 body carries no numbers at all).
      void queryClient.invalidateQueries({
        queryKey: workspaceBillingQueries.creditState(workspaceId).queryKey,
      });
    }
    // Surfaced inline with retry — don't also raise the global banner.
    sendAiMessageMutation.reset();
  }

  const retryAiTurn = async (failedTurnId: string) => {
    const turn = failedAiTurns.find((t) => t.id === failedTurnId);
    if (!turn?.messageId || !turn.userMessage) return;
    setFailedAiTurns((prev) => prev.filter((t) => t.id !== failedTurnId));
    // Accept is idempotent on (messageId, persona): a request that did reach
    // the server answers with the turns it already queued.
    await runAiTurns(
      [turn.aiPersona],
      turn.userMessage,
      turn.webSearchEnabled ?? false,
      turn.messageId,
    );
  };

  // CR-141: what the room's durable turns add to the local state — a
  // "thinking" bubble per queued/running turn nobody streams here (after a
  // reload or a navigation, or the next persona of a message), and a failure
  // card per failed turn of the latest human message that left no reply.
  const personaById = useMemo(() => {
    const byId = new Map<string, AiMember>();
    for (const persona of [...(projectAiPersonas ?? []), ...(aiPersonas ?? [])]) {
      byId.set(persona.ai_persona_id, persona);
    }
    return byId;
  }, [projectAiPersonas, aiPersonas]);

  const { turnBubbles, turnFailures } = useMemo(() => {
    const bubbles: StreamingMessage[] = [];
    const failures: FailedAiTurn[] = [];
    const persistedIds = new Set(allMessages.map((message) => message.id));
    // CR-172: a stopped bubble does not hide the failure card of a turn that
    // failed before the Stop landed.
    const localIds = new Set(
      streamingMessages
        .filter(
          (message) =>
            !(
              message.stopped &&
              openAiTurns?.some(
                (turn) => turn.ai_message_id === message.id && turn.status === "failed",
              )
            ),
        )
        .map((message) => message.id),
    );
    // A message whose accept is still in flight is covered by its pending bubble.
    const pendingHumanIds = new Set(
      streamingMessages
        .filter((message) => message.id === pendingBubbleId(message.humanMessageId ?? ""))
        .map((message) => message.humanMessageId),
    );
    // By created_at, not array order: realtime inserts append, refetches reorder.
    const latestHumanMessageId = allMessages
      .filter((message) => message.actor_type !== "ai")
      .reduce<(typeof allMessages)[number] | undefined>(
        (latest, message) =>
          !latest || (message.created_at ?? "") > (latest.created_at ?? "") ? message : latest,
        undefined,
      )?.id;

    for (const turn of openAiTurns ?? []) {
      const persona = turn.ai_persona_id ? personaById.get(turn.ai_persona_id) : undefined;
      if (!persona || !turn.id || !turn.ai_message_id) continue;
      if (persistedIds.has(turn.ai_message_id) || localIds.has(turn.ai_message_id)) continue;

      if (isOpenTurn(turn)) {
        if (pendingHumanIds.has(turn.human_message_id ?? undefined)) continue;
        // CR-172: a turn being stopped (here, or before a reload) shows the
        // stopped placeholder, never a thinking bubble.
        bubbles.push(
          stoppedIds.has(turn.ai_message_id) || turn.cancel_requested_at
            ? stoppedBubble(turn.ai_message_id, persona, roomId, turn.created_at)
            : aiBubble(turn.ai_message_id, persona, roomId, {
                created_at: turn.created_at,
                thinking: true,
              }),
        );
      } else if (turn.status === "failed" && turn.human_message_id === latestHumanMessageId) {
        failures.push({
          id: turn.id,
          aiPersona: persona,
          message: "The assistant could not answer this message.",
          kind: classifyTurnFailure(turn.last_error),
          retryable: false,
        });
      }
    }
    return { turnBubbles: bubbles, turnFailures: failures };
  }, [openAiTurns, allMessages, streamingMessages, personaById, roomId, stoppedIds]);

  // CR-172: the open turns the current user can stop — queued or running
  // turns answering one of their own messages (Q3: requester only) with no
  // reply in the room yet. `stopping` ones already carry a Stop (from this
  // tab, or a flag set before a reload) that has not failed.
  const { activeTurns, stoppingTurns, visibleStreamingMessages } = useMemo(() => {
    const persistedIds = new Set(allMessages.map((message) => message.id));
    const failedIds = new Set(
      (openAiTurns ?? [])
        .filter((turn) => turn.status === "failed")
        .map((turn) => turn.ai_message_id),
    );
    const actorId = containerMember?.actor_id;
    const ownHumanIds = new Set<string | null>(
      allMessages
        .filter((message) => message.actor_type !== "ai" && message.actor_id === actorId)
        .map((message) => message.id),
    );
    // A request stopped before the server named its turns: its rows are
    // already being stopped (the cancel follows as soon as they are known).
    const stoppedHumanIds = new Set<string | null>();
    for (const message of streamingMessages) {
      if (message.humanMessageId) ownHumanIds.add(message.humanMessageId);
      if (message.humanMessageId && message.stopped) stoppedHumanIds.add(message.humanMessageId);
    }
    const active: AiChatTurnStatusView[] = [];
    const stopping: AiChatTurnStatusView[] = [];
    for (const turn of openAiTurns ?? []) {
      if (!isOpenTurn(turn)) continue;
      if (!turn.id || !turn.ai_message_id || persistedIds.has(turn.ai_message_id)) continue;
      if (!ownHumanIds.has(turn.human_message_id)) continue;
      const stopped =
        stoppedIds.has(turn.ai_message_id) ||
        stoppedHumanIds.has(turn.human_message_id) ||
        !!turn.cancel_requested_at;
      (stopped ? stopping : active).push(turn);
    }
    return {
      activeTurns: active,
      stoppingTurns: stopping,
      // A stopped bubble gives way to the persisted row (the placeholder, or
      // a reply saved before the Stop landed) and to a failed turn's card.
      visibleStreamingMessages: streamingMessages.filter(
        (message) =>
          !(message.stopped && (persistedIds.has(message.id) || failedIds.has(message.id))),
      ),
    };
  }, [openAiTurns, allMessages, streamingMessages, containerMember, stoppedIds]);

  const personaFor = (aiPersonaId: string | null, fallback?: AiMember): AiMember | undefined =>
    (aiPersonaId ? personaById.get(aiPersonaId) : undefined) ?? fallback;

  const markStopped = (ids: string[]) => {
    for (const id of ids) stoppedIdsRef.current.add(id);
    setStoppedIds(new Set(stoppedIdsRef.current));
  };

  /**
   * CR-172: swap the bubbles of the given turns (and the request's pending
   * bubble, if any) for "Response stopped" placeholders, dropping any partial
   * text. Turns the room does not know keep no bubble.
   */
  const showStoppedTurns = (turns: TurnToStop[], request?: LocalAiRequest) => {
    markStopped([
      ...turns.map((turn) => turn.aiMessageId),
      ...(request ? [request.pendingId, request.bubbleId] : []),
    ]);
    setStreamingMessages((prev) => {
      const replaced = new Set<string | null>(turns.map((turn) => turn.aiMessageId));
      if (request) replaced.add(request.pendingId);
      const createdAt = new Map(
        prev
          .filter((message) => replaced.has(message.id))
          .map((message) => [message.id, message.created_at]),
      );
      const fallbackCreatedAt = request ? createdAt.get(request.pendingId) : undefined;
      const bubbles = turns.flatMap((turn) => {
        const persona = personaFor(turn.aiPersonaId, request?.personas[0]);
        return persona
          ? [
              stoppedBubble(
                turn.aiMessageId,
                persona,
                roomId,
                createdAt.get(turn.aiMessageId) ?? turn.createdAt ?? fallbackCreatedAt,
              ),
            ]
          : [];
      });
      return [...prev.filter((message) => !replaced.has(message.id)), ...bubbles];
    });
  };

  /**
   * CR-172: post the cancel. A turn whose cancel failed (`error`, or
   * `not_found` — the server does not know it as ours) is un-stopped: its
   * row shows as running again and Stop is offered again.
   */
  const postCancel = async (turns: TurnToStop[], requests: LocalAiRequest[]) => {
    if (!roomId || !containerMember) return;
    setCancelsInFlight((count) => count + 1);
    try {
      const results = await cancelAiTurns({
        roomId,
        containerId: projectId,
        actorId: containerMember.actor_id,
        turnIds: turns.map((turn) => turn.turnId),
      });
      const failedIds = new Set(
        turns
          .filter((turn) => {
            const result = results.get(turn.turnId);
            return result === "error" || result === "not_found";
          })
          .map((turn) => turn.aiMessageId),
      );
      if (failedIds.size) {
        for (const id of failedIds) stoppedIdsRef.current.delete(id);
        setStoppedIds(new Set(stoppedIdsRef.current));
        setStreamingMessages((prev) =>
          prev.filter((message) => !(message.stopped && failedIds.has(message.id ?? ""))),
        );
      }
    } finally {
      setCancelsInFlight((count) => count - 1);
      // The observer streams have nothing left to show: the turn rows and
      // the saved messages (by realtime) are the source of truth from here.
      for (const request of requests) request.observer.abort();
    }
  };

  /**
   * CR-172: the request's turns are known — show them stopped and cancel
   * them. A function declaration: the send mutation above calls it.
   */
  function stopRequestTurns(request: LocalAiRequest, turns: TurnToStop[]) {
    request.stopSettled = true;
    showStoppedTurns(turns, request);
    if (turns.length) {
      void postCancel(turns, [request]);
    } else {
      request.observer.abort();
    }
  }

  /**
   * CR-172: Stop every AI reply the current user has in flight in this room.
   * The partial text goes at once; the server stops the turns and saves a
   * "Response stopped" row for each. A request the server has not accepted
   * yet is cancelled as soon as its turns are known.
   */
  const stopAiTurns = () => {
    const toCancel = new Map<string, TurnToStop>();
    const requests: LocalAiRequest[] = [];
    for (const request of localRequestsRef.current.values()) {
      if (request.stopRequested) continue;
      request.stopRequested = true;
      if (request.turns.length) {
        request.stopSettled = true;
        requests.push(request);
        const turns = request.turns.map(toTurnToStop);
        showStoppedTurns(turns, request);
        for (const turn of turns) toCancel.set(turn.turnId, turn);
      } else {
        // Not accepted yet: cancelled once the turns are known.
        markStopped([request.pendingId]);
        const persona = request.personas[0];
        setStreamingMessages((prev) =>
          prev.map((message) =>
            message.id === request.pendingId
              ? {
                  ...stoppedBubble(message.id, persona, roomId, message.created_at),
                  // Still covers the message's turn rows until they are known.
                  humanMessageId: request.messageId,
                }
              : message,
          ),
        );
      }
    }
    const rowTurns = activeTurns
      .flatMap(rowToTurnToStop)
      .filter((turn) => !toCancel.has(turn.turnId));
    if (rowTurns.length) showStoppedTurns(rowTurns);
    for (const turn of rowTurns) toCancel.set(turn.turnId, turn);
    if (toCancel.size) void postCancel([...toCancel.values()], requests);
  };

  const onSendMessage = async ({
    content,
    attachments,
    webSearchEnabled = false,
    messageId = crypto.randomUUID(),
  }: {
    content: string;
    attachments: File[];
    webSearchEnabled?: boolean;
    messageId?: string;
  }) => {
    if (!aiPersonas) return false;

    // CR-25: AI Chats are AI-first — when no persona is @mentioned, the active
    // persona (a room member) replies. Message rooms are human-first — the AI
    // replies only when explicitly @mentioned (the server enforces this too).
    const isAiChat = roomContainerType !== "message_room";
    // Participation (CR-25 follow-up): a persona is @mentionable when the room
    // shares all project personas (isAiShared) or the persona is an explicit
    // room member. Same rule for both room types.
    const mentionCandidates = getMentionCandidates({
      isAiShared: isAiShared ?? false,
      projectAiPersonas: projectAiPersonas ?? [],
      roomAiPersonas: aiPersonas ?? [],
    });
    const mentionedAiPersonas = mentionCandidates.filter((member) =>
      isHandleMentioned(content, member.ai_persona_handle),
    );
    // The active persona is resolved against the SAME participating list as
    // mentions (CR-46): in an is_ai_shared room the personas have no
    // container_members row, so resolving against the explicit members only
    // would leave a default set on an implicit persona impossible to route to.
    const respondingAiPersonas = selectRespondingPersonas({
      isAiChat,
      mentionedAiPersonas,
      activePersona: getActivePersona(mentionCandidates, defaultAiPersonaId),
    });

    const result = await sendHumanMessageMutation.mutateAsync({
      content,
      attachments,
      messageId,
    });
    const userMessage = result?.userMessage;

    if (respondingAiPersonas.length > 0 && userMessage) {
      // CR-141: ONE request for every responding persona — the server queues
      // a durable turn each and answers them one after the other.
      await runAiTurns(respondingAiPersonas, userMessage, webSearchEnabled, messageId);
    }

    return !!userMessage;
  };

  const isSending = sendHumanMessageMutation.isPending;
  // True while an AI request is in flight — used to prevent a retry double-fire
  // (isSending only reflects the human-message mutation).
  const isAiResponding = sendAiMessageMutation.isPending;
  // CR-172: a local request still streaming (or not yet accepted) that was not stopped.
  const hasActiveLocalRequest = streamingMessages.some(
    (message) => !!message.humanMessageId && !message.stopped,
  );
  // A request stopped before the server named its turns (its cancel follows).
  const hasUnsettledStop = streamingMessages.some(
    (message) => !!message.humanMessageId && message.stopped,
  );
  const isStoppingAiTurn =
    cancelsInFlight > 0 ||
    hasUnsettledStop ||
    (stoppingTurns.length > 0 && activeTurns.length === 0 && !hasActiveLocalRequest);
  const isAiTurnActive = activeTurns.length > 0 || hasActiveLocalRequest || isStoppingAiTurn;
  const sendError = sendHumanMessageMutation.error ?? sendAiMessageMutation.error;
  const resetSendError = () => {
    sendHumanMessageMutation.reset();
    sendAiMessageMutation.reset();
  };

  return {
    onSendMessage,
    sendAskUserAnswer,
    isSending,
    isAiResponding,
    sendError,
    resetSendError,
    streamingMessages: turnBubbles.length
      ? [...turnBubbles, ...visibleStreamingMessages]
      : visibleStreamingMessages,
    workspaceId,
    creditWarning,
    failedAiTurns: turnFailures.length ? [...failedAiTurns, ...turnFailures] : failedAiTurns,
    retryAiTurn,
    isAiTurnActive,
    isStoppingAiTurn,
    onStopAiTurns: stopAiTurns,
  };
}
